import {
  Button,
  IconChevronDown,
  IconEdit3,
  IconPlus,
  Popover,
} from '@odekoteam/doppio';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Flex, Select, ThemeUICSSObject } from 'theme-ui';

import IconSave from '@/assets/images/IconSave.svg?icon';
import IconUndo from '@/assets/images/IconUndo.svg?icon';
import { CatalogMasterHomepageRows } from '@/components/Collection/CatalogMasterHomepageRows';
import { HomepageActionsMenu } from '@/components/Collection/HomepageActionsMenu';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import { ModalPreview } from '@/components/Collection/ModalPreview';
import { ModalReference } from '@/components/Collection/ModalReference';
import { ModalUpsertHomepage } from '@/components/Collection/ModalUpsertHomepage';
import {
  GetWarehousesQuery,
  useGetMasterHomepageForEditQuery,
  useGetMasterHomepagesQuery,
} from '@/graphql/types/generated';

const TOOLBAR_COLLAPSE_QUERY = '@media (max-width: 1400px)';

const menuActionWrapperStyles: ThemeUICSSObject = {
  width: '100%',
  '& > *': {
    width: '100% !important',
    justifyContent: 'flex-start !important',
  },
};

// eslint-disable-next-line complexity
export default function CatalogMasterHomepageTab({
  warehouses,
  totalWarehouses,
}: {
  totalWarehouses: number;
  warehouses: GetWarehousesQuery['warehouses'][0][];
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [saveVersion, setSaveVersion] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [
    { data: homepagesData, fetching: isHomepagesDataLoading },
    reexecuteMasterHomepages,
  ] = useGetMasterHomepagesQuery({
    requestPolicy: 'cache-and-network',
  });
  const [masterHomepageModalMode, setMasterHomepageModalMode] = useState<
    'create' | 'edit' | null
  >(null);
  const [isRefreshingHomepage, setIsRefreshingHomepage] = useState(false);
  const selectedMasterHomepageId = searchParams.get('masterHomepageId') ?? '';
  const masterHomepages = homepagesData?.masterHomepages ?? [];

  const [
    { data: selectedHomepageData, fetching: isHomepageDataLoading },
    reexecuteHomepageForEdit,
  ] = useGetMasterHomepageForEditQuery({
    pause: !selectedMasterHomepageId,
    requestPolicy: 'cache-and-network',
    variables: { id: selectedMasterHomepageId },
  });
  const selectedMasterHomepage =
    selectedHomepageData?.masterHomepageForEdit ?? null;
  const hasSelectedMasterHomepage =
    !!selectedMasterHomepageId && !!selectedMasterHomepage;
  const isLoading =
    isHomepagesDataLoading || isHomepageDataLoading || isRefreshingHomepage;

  const setSelectedMasterHomepageId = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('masterHomepageId', value);
          } else {
            next.delete('masterHomepageId');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <>
      <Flex
        sx={{
          py: 3,
          gap: 3,
          alignItems: 'center',
          borderBottom: '1px solid',
          justifyContent: 'space-between',
          ...(isEditing && { bg: '#FFFFDF' }),
          flexWrap: isEditing ? 'nowrap' : 'wrap',
          borderBottomColor: isEditing ? '#E0A100' : 'secondaryHighlight',
        }}
      >
        <Flex
          sx={{
            gap: 3,
            minWidth: 0,
            alignItems: 'center',
            flexWrap: isEditing ? 'nowrap' : 'wrap',
            [TOOLBAR_COLLAPSE_QUERY]: !isEditing ? { width: '100%' } : {},
          }}
        >
          <Select
            disabled={isEditing}
            value={selectedMasterHomepageId}
            sx={{
              pl: 3,
              pr: 8,
              mr: 1,
              fontWeight: 'bold',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              width: 'fit-content',
              borderColor: 'secondaryHighlight',
              ...(isEditing && { opacity: 0.5 }),
              cursor: isEditing ? 'not-allowed' : 'pointer',
            }}
            onChange={(event) =>
              setSelectedMasterHomepageId(event.target.value)
            }
          >
            <option value="">Select</option>
            {masterHomepages.map((masterHomepage) => (
              <option key={masterHomepage.id} value={masterHomepage.id}>
                {masterHomepage.name}
              </option>
            ))}
          </Select>
          {!isEditing && (
            <>
              <HomepageActionsMenu
                homepage={selectedMasterHomepage}
                masterHomepages={masterHomepages}
                onSaved={(id) => {
                  setSelectedMasterHomepageId(id);
                  reexecuteMasterHomepages();
                  reexecuteHomepageForEdit();
                }}
                onDuplicated={(id) => {
                  setSelectedMasterHomepageId(id);
                  reexecuteMasterHomepages();
                  reexecuteHomepageForEdit();
                }}
                onDeleted={() => {
                  setSelectedMasterHomepageId('');
                  reexecuteMasterHomepages();
                  reexecuteHomepageForEdit();
                }}
                disabled={!hasSelectedMasterHomepage}
              />
              <Button
                size="small"
                variant="primary"
                iconLeft={<IconPlus />}
                onPress={() => setMasterHomepageModalMode('create')}
              >
                <ResponsiveLabel full="New Homepage" compact="New" />
              </Button>
              <Box
                sx={{
                  display: 'none',
                  [TOOLBAR_COLLAPSE_QUERY]: {
                    ml: 'auto',
                    display: 'block',
                  },
                }}
              >
                <HomepageEditOptionsMenu
                  warehouses={warehouses}
                  onEditLayout={() => setIsEditing(true)}
                  isEditLayoutDisabled={!hasSelectedMasterHomepage}
                />
              </Box>
            </>
          )}
        </Flex>
        {!isEditing && (
          <Flex
            sx={{
              gap: 3,
              alignItems: 'center',
              [TOOLBAR_COLLAPSE_QUERY]: { display: 'none' },
            }}
          >
            <ModalReference />
            <ModalPreview warehouses={warehouses} />
          </Flex>
        )}
        {isEditing ? (
          <Flex sx={{ gap: 3, alignItems: 'center' }}>
            <Box sx={{ [TOOLBAR_COLLAPSE_QUERY]: { display: 'none' } }}>
              Editing - drag to reorder, click to modify
            </Box>
            <Button
              size="small"
              variant="ghost"
              iconLeft={<IconUndo />}
              onPress={() => setIsEditing(false)}
            >
              <ResponsiveLabel full="Discard Changes" compact="Discard" />
            </Button>
            <Button
              size="small"
              variant="secondary"
              iconLeft={<IconSave />}
              onPress={() => setSaveVersion((value) => value + 1)}
            >
              <ResponsiveLabel full="Save Changes" compact="Save" />
            </Button>
          </Flex>
        ) : (
          <Box
            sx={{
              [TOOLBAR_COLLAPSE_QUERY]: { display: 'none' },
            }}
          >
            <Button
              size="small"
              variant="secondary"
              iconLeft={<IconEdit3 />}
              onPress={() => setIsEditing(true)}
              isDisabled={!hasSelectedMasterHomepage}
            >
              Edit Layout
            </Button>
          </Box>
        )}
      </Flex>
      <Box sx={{ pt: 4 }}>
        {isLoading && <LoadingBox />}
        {!isLoading && !selectedMasterHomepageId && (
          <div
            sx={{
              p: 5,
              fontStyle: 'italic',
              textAlign: 'center',
            }}
          >
            Please select a homepage from the dropdown above or create a new
            one.
          </div>
        )}
        {!isLoading && selectedMasterHomepageId && selectedMasterHomepage && (
          <CatalogMasterHomepageRows
            isEditing={isEditing}
            saveVersion={saveVersion}
            warehouses={warehouses}
            totalWarehouses={totalWarehouses}
            homepage={selectedMasterHomepage}
            onSaved={async () => {
              setIsRefreshingHomepage(true);
              try {
                await reexecuteHomepageForEdit({
                  requestPolicy: 'network-only',
                });
                setIsEditing(false);
              } finally {
                setIsRefreshingHomepage(false);
              }
            }}
          />
        )}
      </Box>
      <ModalUpsertHomepage
        mode={masterHomepageModalMode}
        homepage={selectedMasterHomepage}
        masterHomepages={masterHomepages}
        close={() => setMasterHomepageModalMode(null)}
        onSaved={(id) => {
          setSelectedMasterHomepageId(id);
          reexecuteMasterHomepages();
          reexecuteHomepageForEdit();
        }}
      />
    </>
  );
}

function ResponsiveLabel({ full, compact }: { full: string; compact: string }) {
  return (
    <>
      <Box as="span" sx={{ [TOOLBAR_COLLAPSE_QUERY]: { display: 'none' } }}>
        {full}
      </Box>
      <Box
        as="span"
        sx={{
          display: 'none',
          [TOOLBAR_COLLAPSE_QUERY]: { display: 'inline' },
        }}
      >
        {compact}
      </Box>
    </>
  );
}

function HomepageEditOptionsMenu({
  warehouses,
  onEditLayout,
  isEditLayoutDisabled,
}: {
  onEditLayout: () => void;
  isEditLayoutDisabled: boolean;
  warehouses: GetWarehousesQuery['warehouses'][0][];
}) {
  return (
    <Popover.Root>
      <Popover.Trigger>
        <Button size="small" variant="tertiary" iconRight={<IconChevronDown />}>
          Edit Options
        </Button>
      </Popover.Trigger>
      <Popover.Content align="right">
        <Box
          sx={{
            m: -2,
            display: 'flex',
            width: 'max-content',
            flexDirection: 'column',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <ModalReference
            triggerVariant="ghost"
            triggerWrapperSx={menuActionWrapperStyles}
          />
          <ModalPreview
            warehouses={warehouses}
            triggerVariant="ghost"
            triggerWrapperSx={menuActionWrapperStyles}
          />
          <Box sx={menuActionWrapperStyles}>
            <Button
              size="small"
              variant="ghost"
              iconLeft={<IconEdit3 />}
              onPress={onEditLayout}
              isDisabled={isEditLayoutDisabled}
            >
              Edit Layout
            </Button>
          </Box>
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}
